/** Named Show: presets. Custom is stored separately with its start and end dates. */
export const PRACTICE_LEDGER_DATE_RANGE_PRESET = {
  CurrentMonth: "current-month",
  Last30Days: "last-30-days",
  Last90Days: "last-90-days",
  Custom: "custom",
} as const;

export type PracticeLedgerDateRangePreset =
  (typeof PRACTICE_LEDGER_DATE_RANGE_PRESET)[keyof typeof PRACTICE_LEDGER_DATE_RANGE_PRESET];

/** Presets that resolve to a range from `now` without a user-chosen start and end. */
export type PracticeLedgerNamedDateRangePreset = Exclude<
  PracticeLedgerDateRangePreset,
  typeof PRACTICE_LEDGER_DATE_RANGE_PRESET.Custom
>;

export const PRACTICE_LEDGER_DATE_RANGE_PRESET_LABEL: Record<
  PracticeLedgerDateRangePreset,
  string
> = {
  [PRACTICE_LEDGER_DATE_RANGE_PRESET.CurrentMonth]: "Current Month",
  [PRACTICE_LEDGER_DATE_RANGE_PRESET.Last30Days]: "Last 30 days",
  [PRACTICE_LEDGER_DATE_RANGE_PRESET.Last90Days]: "Last 90 days",
  [PRACTICE_LEDGER_DATE_RANGE_PRESET.Custom]: "Custom range",
};

/** Inclusive calendar-day counts for the Last 30 / Last 90 presets, counting today as day 1. */
export const PRACTICE_LEDGER_LAST_30_DAY_COUNT = 30;
export const PRACTICE_LEDGER_LAST_90_DAY_COUNT = 90;

export interface PracticeLedgerDateRange {
  startDate: Date;
  endDate: Date;
}

/**
 * The Show: choice the Ledger keeps. A named preset is stored by name so its window follows the
 * calendar day; a custom range keeps the bounds the user picked.
 */
export type PracticeLedgerDateRangeSelection =
  | { preset: PracticeLedgerNamedDateRangePreset }
  | { preset: typeof PRACTICE_LEDGER_DATE_RANGE_PRESET.Custom; dateRange: PracticeLedgerDateRange };

/** The Ledger Show: default (brief 4.7), stored by name so it follows the calendar day. */
export const DEFAULT_PRACTICE_LEDGER_DATE_RANGE_SELECTION = {
  preset: PRACTICE_LEDGER_DATE_RANGE_PRESET.Last30Days,
} as const satisfies PracticeLedgerDateRangeSelection;

export const PRACTICE_LEDGER_ROW_TYPE = {
  Charge: "Charge",
  Payment: "Payment",
  PaymentReversal: "Payment Reversal",
  Adjustment: "Adjustment",
} as const;

export type PracticeLedgerRowType =
  (typeof PRACTICE_LEDGER_ROW_TYPE)[keyof typeof PRACTICE_LEDGER_ROW_TYPE];

export const PRACTICE_LEDGER_SEARCH_OPTION = {
  Client: "client",
  Insurance: "insurance",
} as const;

export type PracticeLedgerSearchOption =
  (typeof PRACTICE_LEDGER_SEARCH_OPTION)[keyof typeof PRACTICE_LEDGER_SEARCH_OPTION];

export const PRACTICE_LEDGER_SOURCE_KIND = {
  Client: "client",
  Insurance: "insurance",
  Practice: "practice",
} as const;

export type PracticeLedgerSourceKind =
  (typeof PRACTICE_LEDGER_SOURCE_KIND)[keyof typeof PRACTICE_LEDGER_SOURCE_KIND];

/**
 * One Recent Activity row: a Charge (Invoice), or a Payment, Payment Reversal or Adjustment
 * (PaymentReconciliation detail).
 */
export interface PracticeLedgerRow {
  id: string;
  clientId: string;
  clientName: string;
  medicalRecordNumber: string;
  /** Statement `invoice-identifier`, when the row is tied to a statement. */
  statementId: string | undefined;
  type: PracticeLedgerRowType;
  /**
   * FHIR calendar date (`YYYY-MM-DD`). A charge uses `Invoice.date`. A payment, payment reversal or
   * adjustment uses its PaymentReconciliation's `paymentDate`, or `created` when that is missing.
   */
  transactionDate: string | undefined;
  /** Self-Pay or the payer organization name. */
  sourceLabel: string;
  sourceKind: PracticeLedgerSourceKind;
  /**
   * USD, as cash in and out: a payment is positive and a payment reversal is negative. An
   * adjustment keeps its stored sign, where negative lowers what the client owes.
   */
  amount: number;
}

/** Cash KPIs for the selected Show: window. Payment reversals enter as negative payments. */
export interface PracticeLedgerTotals {
  totalCharges: number;
  totalReceived: number;
  fromClients: number;
  fromInsurance: number;
}

export const EMPTY_PRACTICE_LEDGER_TOTALS: PracticeLedgerTotals = {
  totalCharges: 0,
  totalReceived: 0,
  fromClients: 0,
  fromInsurance: 0,
};

export const PRACTICE_LEDGER_PAGE_SIZE = 20;

export const PRACTICE_LEDGER_SORT_COLUMN = {
  TransactionDate: "transaction-date",
} as const;

export type PracticeLedgerSortColumn =
  (typeof PRACTICE_LEDGER_SORT_COLUMN)[keyof typeof PRACTICE_LEDGER_SORT_COLUMN];

export const PRACTICE_LEDGER_SORT_DIRECTION = {
  Asc: "asc",
  Desc: "desc",
} as const;

export type PracticeLedgerSortDirection =
  (typeof PRACTICE_LEDGER_SORT_DIRECTION)[keyof typeof PRACTICE_LEDGER_SORT_DIRECTION];

export interface PracticeLedgerSortState {
  column: PracticeLedgerSortColumn;
  direction: PracticeLedgerSortDirection;
}

export const DEFAULT_PRACTICE_LEDGER_SORT: PracticeLedgerSortState = {
  column: PRACTICE_LEDGER_SORT_COLUMN.TransactionDate,
  direction: PRACTICE_LEDGER_SORT_DIRECTION.Desc,
};

export interface PracticeLedgerSearch {
  query: string;
  option: PracticeLedgerSearchOption;
}

export interface PracticeLedgerPage {
  rows: PracticeLedgerRow[];
  /** Row count after search, before paging — sizes the pager. */
  totalRows: number;
  /** Payment and payment reversal totals for the Show: window; search and paging do not change them. */
  totals: PracticeLedgerTotals;
}

/** Loaded page plus hook status for the practice Ledger table. */
export interface UsePracticeLedgerTableDataResult extends PracticeLedgerPage {
  isLoading: boolean;
  error: Error | undefined;
}

export type LoadPracticeLedgerResult =
  | { ok: true; data: PracticeLedgerPage }
  | { ok: false; reason: "missing_practice_id" | "load_failed"; details?: string };

export interface PracticeLedgerCsvExport {
  csvContent: string;
  filename: string;
  rowCount: number;
}

export type ExportPracticeLedgerCsvResult =
  | { ok: true; data: PracticeLedgerCsvExport }
  | { ok: false; reason: "missing_practice_id" | "load_failed"; details?: string };
